/** @file Replaces the whole document (pose2d push / PUT doc): migrated, validated, one commit. */
import { z } from 'zod';
import { migrate } from '@pose2d/format/migrations/migrate';
import { freshProblems } from '@pose2d/format/doc/freshProblems';
import { DocSchema } from '@pose2d/format/doc/schema';
import { ids } from '@pose2d/format/ids';
import { names } from '@pose2d/format/names';
import { validateDoc } from '@pose2d/format/doc/validateDoc';
import { throwError } from '@pose2d/format/throwError';
import { toPose2DError } from '@pose2d/format/toPose2DError';
import type { Doc, Pose2DError } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import { currentOf } from '../_shared/currentOf';
import { sameJson } from '../animation/sameJson';

/** Rethrows a document error with its path moved under the `doc` input field. */
function underDoc(err: Pose2DError): never {
  return throwError(err.code, err.params, { path: err.path === undefined ? 'doc' : `doc.${err.path}`, hint: err.hint ?? err.message });
}

/** Migrates `raw` to the current schema version and parses it with DocSchema (errors point into `doc`). */
function migrated(raw: unknown): Doc {
  try {
    return migrate(raw);
  } catch (e) {
    const err = toPose2DError(e);
    if (err === null) throw e;
    return underDoc(err);
  }
}

/**
 * The top-level fields whose JSON differs between `head` and `next` (one missing on either side counts). A field that
 * differs only in the property order of its records is compared again on `head` parsed with DocSchema, which puts
 * every record in schema order as `migrate` did for `next`; table key order (the entity order) still counts.
 */
function changedFields(head: Doc, next: Doc): string[] {
  const a = head as unknown as Record<string, unknown>;
  const b = next as unknown as Record<string, unknown>;
  let parsed: Record<string, unknown> | null | undefined;
  return [...new Set([...Object.keys(a), ...Object.keys(b)])].filter((key) => {
    if (sameJson(a[key], b[key])) return false;
    if (parsed === undefined) {
      const result = DocSchema.safeParse(head);
      parsed = result.success ? result.data : null;
    }
    return parsed === null || !sameJson(parsed[key], b[key]);
  });
}

/**
 * The name, createdAt and updatedAt the replaced document keeps. The name is always the stored one: replace_doc never
 * renames a project (R23-06; project.rename is the only way, with its NAME_TAKEN and name checks). A stored name that
 * is not a usable project name (the name rule, and not shaped like an id: SEC-3 reserves those) is refused; the
 * Worker's PUT /doc puts the project's index name there first (R23-07). A stored time that is not a string comes from
 * the incoming document, so a repair carries no damage forward.
 */
function keptMeta(stored: unknown, incoming: Doc['meta']): Pick<Doc['meta'], 'name' | 'createdAt' | 'updatedAt'> {
  const meta = (typeof stored === 'object' && stored !== null ? stored : {}) as Partial<Record<'name' | 'createdAt' | 'updatedAt', unknown>>;
  const text = (value: unknown, fallback: string): string => (typeof value === 'string' ? value : fallback);
  if (!(typeof meta.name === 'string' && names.isValid('project', meta.name) && !ids.isId(meta.name))) {
    throwError('VALIDATION', undefined, { path: 'doc.meta.name', hint: 'the stored project name is unusable and replace_doc keeps the project name; repair the project through the REST document endpoint, which keeps the project index name' });
  }
  return { name: meta.name, createdAt: text(meta.createdAt, incoming.createdAt), updatedAt: text(meta.updatedAt, incoming.updatedAt) };
}

/** project.replaceDoc: only the top-level fields that differ are written (R17-01d); an equal document writes nothing. */
export const command = defineCommand({
  name: 'project.replaceDoc',
  description: 'Replace the whole document with `doc` (any stored pose2d.doc version, e.g. from pose2d pull): migrated, then validated; the project keeps its name and createdAt (use project.rename).',
  input: z.object({ doc: z.unknown() }).strict(),
  apply(draft, input, ctx) {
    const doc = migrated(input.doc);
    const problems = validateDoc(doc);
    // Only problems the replaced document did not already have refuse (R23-01): a failing head stays repairable by
    // PUT /doc. The old document is validated only when the new one has problems, so a healthy project pays nothing.
    const [first] = problems.length === 0 ? [] : freshProblems(validateDoc(currentOf(draft)), problems);
    if (first) underDoc(first);
    const { name, createdAt, updatedAt } = keptMeta(currentOf(draft).meta, doc.meta);
    if (doc.meta.name !== name) {
      ctx.warnings.push({ code: 'INFO', message: `project name "${name}" was kept; use project.rename to change it`, params: { from: doc.meta.name, to: name }, path: 'doc.meta.name' });
    }
    const next: Doc = { ...doc, meta: { ...doc.meta, name, createdAt, updatedAt } };
    const changed = changedFields(currentOf(draft), next);
    if (changed.length === 0) return;
    for (const key of changed) {
      if (key in next) Reflect.set(draft, key, (next as unknown as Record<string, unknown>)[key]);
      else Reflect.deleteProperty(draft, key);
    }
    draft.meta.updatedAt = ctx.now;
  },
});
